import { useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { useEventSlug } from '@/shared/hooks/useEventSlug'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { Button } from '@/shared/ui/Button'

import { QuestAvailabilityState } from './QuestAvailabilityState'
import { QuestDoneCelebration } from './QuestDoneCelebration'
import { QuestFormStep } from './QuestFormStep'
import { QuestQuizStep } from './QuestQuizStep'
import { QuestRewardPicker } from './QuestRewardPicker'
import { QuestRunStep } from './QuestRunStep'
import { QuestTotemShell } from './QuestTotemShell'
import { QuestWelcomeStep } from './QuestWelcomeStep'
import { TotemPatternGrid } from './TotemPatternGrid'
import { classifyCreateRunError } from '../model/createRunError'
import { useEventBrandingQuery } from '../model/queries'
import { useQuestPlayerMachine } from '../model/useQuestPlayerMachine'

import type { QuestRun, QuestShellVariant } from '../model/types'

type QuestPlayerPageProps = {
  questUid: string
  shellVariant: QuestShellVariant
}

export function QuestPlayerPage({ questUid, shellVariant }: QuestPlayerPageProps) {
  const { t } = useTranslation()
  const eventSlug = useEventSlug()
  const { data: branding } = useEventBrandingQuery(eventSlug)
  const machine = useQuestPlayerMachine(eventSlug, questUid)
  const { state } = machine

  const [exitOpen, setExitOpen] = useState(false)
  const [duplicateIdentifierOpen, setDuplicateIdentifierOpen] = useState(false)
  const usesQuestShell = !['loading', 'error', 'blocked'].includes(state.phase)

  const handleSubmitStartRun = async (values: Parameters<typeof machine.submitStartRun>[0]) => {
    try {
      await machine.submitStartRun(values)
    } catch (error) {
      if (classifyCreateRunError(error) === 'duplicate_identifier') {
        setDuplicateIdentifierOpen(true)
      }
    }
  }

  const handleQuizFinished = (_run: QuestRun) => {
    void machine.finish()
  }

  const isTotemDevice = state.quest?.totem_unlock_mode === 'pattern_grid'

  if (state.phase === 'loading') return null
  if (state.phase === 'error') return <p role="alert">{t(state.errorMessage ?? 'quests.errors.generic')}</p>
  if (state.phase === 'blocked') return <p role="alert">{t('quests.errors.blocked')}</p>

  const content = (
    <>
      {state.phase === 'totem' && (
        <TotemPatternGrid
          errorMessage={machine.unlockError ? t('quests.totem.invalidPattern') : null}
          busy={machine.unlockPending}
          onSubmit={machine.unlock}
        />
      )}

      {(state.phase === 'standby' || state.phase === 'closed') && (
        <QuestAvailabilityState
          mode={state.phase === 'standby' ? 'soon' : 'closed'}
          startAt={state.quest?.start_at}
          onOpen={machine.reload}
        />
      )}

      {state.phase === 'welcome' && state.quest && (
        <QuestWelcomeStep quest={state.quest} onStart={machine.start} />
      )}

      {state.phase === 'play' && state.quest && (
        <QuestFormStep
          formInfos={state.quest.quest_form_infos}
          uniquenessPolicy={state.quest.uniqueness_policy}
          busy={machine.submitStartRunPending}
          onSubmit={handleSubmitStartRun}
        />
      )}

      {state.phase === 'quiz' && state.quest && state.run && (
        <QuestQuizStep
          eventSlug={eventSlug}
          questUid={questUid}
          runId={state.run.id}
          onFinished={handleQuizFinished}
        />
      )}

      {state.phase === 'run' && <QuestRunStep busy={machine.finishPending} onContinue={() => void machine.finish()} />}

      {state.phase === 'choose_reward' && state.quest && state.run && (
        <QuestRewardPicker
          rewards={state.quest.eligible_rewards}
          runScore={state.run.score}
          busy={machine.chooseRewardPending}
          onRedeem={id => void machine.chooseReward(id)}
        />
      )}

      {state.phase === 'done' && state.quest && (
        <QuestDoneCelebration
          uniquenessPolicy={state.quest.uniqueness_policy}
          autoRestart={isTotemDevice}
          onRestart={machine.restart}
        />
      )}
    </>
  )

  return (
    <>
      {usesQuestShell ? (
        <QuestTotemShell
          variant={shellVariant}
          logoSrc={branding?.logo?.file_url}
          brandColor={branding?.brand_color}
          footer={
            <Button variant="ghost" size="sm" onClick={() => setExitOpen(true)}>
              {t('quests.player.exit')}
            </Button>
          }
        >
          {content}
        </QuestTotemShell>
      ) : (
        content
      )}

      <AlertDialog open={exitOpen} onOpenChange={setExitOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('quests.player.exitConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('quests.player.exitConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('quests.player.exitConfirmCancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => window.history.back()}>
              {t('quests.player.exitConfirmConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={duplicateIdentifierOpen} onOpenChange={setDuplicateIdentifierOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('quests.form.duplicateIdentifierTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('quests.form.duplicateIdentifierDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setDuplicateIdentifierOpen(false)}>
              {t('quests.player.exitConfirmCancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
